import { useState, type ReactNode } from "react";
import { SlidersHorizontal } from "lucide-react";
import { Button } from "../../client/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../../client/components/ui/sheet";
import useTranslation from "../../client/hooks/useTranslation";
import TopbarSlotRenderer, { type TopbarSlotRendererProps } from "./TopbarSlotRenderer";
import type { SlotPlacement, WidgetKey } from "./types";

// Slots qui restent toujours inline (action principale), jamais regroupés dans le Sheet.
const PRIMARY_ACTION_KEYS: WidgetKey[] = ["topbar-add-trade", "topbar-add-payout"];

// Map widgetKey -> chemin de label i18n existant (sous dashboard.widgets), pour
// l'étiquette affichée au-dessus de chaque contrôle dans le Sheet.
function useFilterLabel() {
  const t = useTranslation();
  const w = t.dashboard.widgets;
  const labels: Partial<Record<WidgetKey, string>> = {
    "topbar-date-filter": w.topbarDateFilter,
    "topbar-account-filter": w.topbarAccountFilter,
    "topbar-strategy-filter": w.topbarStrategyFilter,
    "topbar-pfm-filter": w.topbarPfmFilter,
    "topbar-phase-filter": w.topbarPhaseFilter,
    "topbar-symbol-filter": w.topbarSymbolFilter,
    "topbar-side-filter": w.topbarSideFilter,
    "topbar-status-filter": w.topbarStatusFilter,
    "topbar-pnl-filter": w.topbarPnlFilter,
    "topbar-tags-filter": w.topbarTagsFilter,
  };
  return (key: WidgetKey): string => labels[key] ?? "";
}

// Props : on reçoit la même bag que TopbarSlotRenderer (hors slot/isEditMode), plus
// la liste des slots et un éventuel extraActions spécifique à la page.
export type TopbarFilterBarProps = Omit<TopbarSlotRendererProps, "slot" | "isEditMode"> & {
  slots: SlotPlacement[];
  extraActions?: ReactNode;
};

export default function TopbarFilterBar({ slots, extraActions, ...slotProps }: TopbarFilterBarProps) {
  const t = useTranslation();
  const getLabel = useFilterLabel();
  const [open, setOpen] = useState(false);

  const primarySlots = slots.filter((s) => PRIMARY_ACTION_KEYS.includes(s.widgetKey));
  const filterSlots = slots.filter((s) => !PRIMARY_ACTION_KEYS.includes(s.widgetKey));

  return (
    <>
      {/* >= lg : rendu inline identique à l'existant (tous les slots dans l'ordre d'origine) */}
      <div className="hidden lg:flex items-center gap-2 flex-wrap justify-end">
        {slots.map((slot) => (
          <TopbarSlotRenderer key={slot.id} slot={slot} isEditMode={false} {...slotProps} />
        ))}
        {extraActions}
      </div>

      {/* < lg : bouton Filtres (Sheet) + actions primaires inline */}
      <div className="flex lg:hidden items-center gap-2">
        {filterSlots.length > 0 && (
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 gap-1.5">
                <SlidersHorizontal className="h-4 w-4" />
                {t.pages.topbarFilters}
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
              <SheetHeader>
                <SheetTitle>{t.pages.topbarFiltersTitle}</SheetTitle>
              </SheetHeader>
              <div className="flex flex-col gap-4 py-4">
                {filterSlots.map((slot) => {
                  const label = getLabel(slot.widgetKey);
                  return (
                    <div key={slot.id} className="flex flex-col gap-1.5">
                      {label && (
                        <span className="text-xs font-medium text-muted-foreground">{label}</span>
                      )}
                      <TopbarSlotRenderer slot={slot} isEditMode={false} {...slotProps} />
                    </div>
                  );
                })}
                {extraActions && (
                  // Fermer le Sheet quand une action est déclenchée (sélecteur de layout,
                  // Personnaliser) pour éviter d'empiler une modale par-dessus le Sheet ouvert.
                  <div
                    className="flex flex-col gap-2 border-t border-border pt-4"
                    onClick={() => setOpen(false)}
                  >
                    {extraActions}
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>
        )}
        {/* Pas de filtres (donc pas de Sheet) : garder extraActions accessible inline */}
        {filterSlots.length === 0 && extraActions}
        {primarySlots.map((slot) => (
          <TopbarSlotRenderer key={slot.id} slot={slot} isEditMode={false} {...slotProps} />
        ))}
      </div>
    </>
  );
}
